/**
 * 史実イベント — 第IV章「天下統一」（1582-1600）
 *
 * docs/01-ゲームデザイン.md §7 の第四章の表に対応する。
 * 表にある5件（本能寺の変・太閤検地・刀狩・小田原征伐＝本データでは「小田原攻め」・文禄の役）に加え、
 * 合戦以外の出来事（バテレン追放令・身分統制令と人掃令・秀吉の死）を3件足し、
 * さらに終章の関ヶ原を1件収めて計9件としてある。
 * docs/05-学習設計.md §4-4 に従い、章の内容が合戦だけに偏らないようにするためである。
 *
 * 【この章の設計方針】
 * 第IV章は「合戦で国を取る時代」が「帳簿と法令で国を治める時代」に
 * 置き換わっていく章である。したがって効果は資源の増減で終わらせず、
 *   ・首位が突然崩れる（本能寺の変 → target:'leader'）
 *   ・領国の値打ちが測り直され、村の守りが薄くなる（太閤検地 → formationMod defend）
 *   ・軍札から僧兵が抜ける（刀狩 → deckRemove）
 *   ・下剋上札が取り上げられる（身分統制令・人掃令 → grantGekokujo -1）
 *   ・兵が海の向こうへ行き、手勢札が減る（文禄の役 → holeCardMod）
 * という形で、史実がルールを書き換えるようにしてある。
 *
 * とくに「下剋上札が配られなくなるどころか取り上げられる」ことは、
 * 第I章から積み上げてきた本作の逆転装置（docs/06 §6）が、
 * 兵農分離と身分の固定によって閉じていく過程そのものである。
 * 第I章で下剋上を手にした感触を覚えているプレイヤーほど、
 * この章で「時代が変わった」ことを強く受け取る。
 *
 * 【執筆基準】docs/05-学習設計.md §4 と provinces/_reference.ts に揃える。
 *   - 武将を「英雄」「悪」と評価しない。戦闘行為を美化・賛美しない
 *   - 宗教勢力（一向一揆・キリスト教・寺社）は事実の記述に留める
 *   - 女性・民衆の存在を消さない。検地・刀狩・普請など生活面の事実を必ず入れる
 *   - 通説と異説がある事項は caveat に必ず書く
 *   - ゲームの都合で史実を改変した箇所は gameNote に必ず書く
 *   - 年号は西暦を主とし、必要に応じて和暦を添える
 *
 * 【章の区切りについて】
 * docs/01 §7 では 1600年の関ヶ原を「終章」として第四章から分けている。
 * 本ファイルは 1582-1600 を担当範囲とするため関ヶ原も収めるが、
 * SEKIGAHARA だけは chapter: 5 とし、章デッキも
 * CHAPTER4_EVENTS（第IV章）と FINALE_EVENTS（終章）に分けてある。
 *
 * 【HALF について】
 * core の HALF 定数（docs/02 §2.6）は -1 である。
 * 本パッケージは '@tenka/core' を直接 import しない方針なので、
 * 半減を表す箇所は数値 -1 を直接書き、必ずコメントで断ってある。
 */

import type { HistoricalEvent } from '../types';

// ---------------------------------------------------------------------------
// 1582 — 本能寺の変（事件札・この章の中核）
// ---------------------------------------------------------------------------

/**
 * docs/01 §7 の指定どおり target:'leader' を使う唯一のイベント。
 * 首位だけが兵の半数（-1 = HALF）と領国1つを失う。
 *
 * 【重要】この機構は「明智光秀が首位を叩く役割だった」という意味ではない。
 * 光秀の動機には定説がなく、ゲームの効果を動機の説明と読まれないよう
 * gameNote で明確に断ってある。
 */
export const HONNOJI_NO_HEN: HistoricalEvent = {
  id: 'ev:honnoji-no-hen',
  year: 1582,
  chapter: 4,
  kind: 'incident',
  name: '本能寺の変',
  reading: 'ほんのうじのへん',
  effects: [
    {
      t: 'target',
      who: 'leader',
      then: [
        // n: -1 は core の HALF（半減・切り捨て）。docs/02 §2.6
        { t: 'lose', res: 'hei', n: -1 },
        { t: 'loseProvince', n: 1 },
      ],
    },
  ],
  note: {
    summary:
      '1582年6月、京都の本能寺に滞在していた織田信長が家臣の明智光秀の軍に襲われ、自害した。嫡男の信忠も二条御所で死亡し、織田政権は急に方向を失った。',
    detail: {
      1: '1582年、京都の本能寺にとまっていた織田信長が、家来の明智光秀の軍にせめられて命を落としました。天下統一まであと少しというときの出来事でした。このあと羽柴秀吉が光秀をやぶり、力をのばしていきます。',
      2: '天正10年6月2日（西暦1582年6月21日）、中国地方へ向かう途中で京都の本能寺に泊まっていた織田信長が、家臣の明智光秀の軍に襲われ自害した。京都の二条御所にいた嫡男の織田信忠も攻められて死亡し、織田家の中枢が一度に失われた。備中高松城で毛利氏と対峙していた羽柴秀吉は、急ぎ和睦して引き返し（中国大返し）、同月の山崎の戦いで光秀を破った。光秀は敗走の途中で命を落としている。翌1583年の賤ヶ岳の戦いで秀吉が柴田勝家を破り、織田家の後継争いは秀吉の主導で収まっていった。',
      3: '天正10年6月2日（西暦1582年6月21日）未明、京都四条西洞院の本能寺に宿していた織田信長が、明智光秀の軍勢に攻められ自害した。二条御所にあった嫡男信忠も攻撃を受けて死亡している。信長は毛利氏と対陣する羽柴秀吉の援軍に向かう途上で、少人数の供回りしか伴っていなかった。事件を知った秀吉は毛利氏と急ぎ和睦して畿内へ取って返し（中国大返し）、6月13日の山崎の戦いで光秀を破った。光秀は近江へ落ちる途上で落命したと伝えられる。光秀の動機については、怨恨説、天下への野望説、朝廷・足利義昭・徳川家康らを黒幕とする諸説、長宗我部氏との取次を務めていた光秀の立場が信長の四国政策転換によって失われたとする四国説などが提示されてきたが、いずれも決め手を欠き、現在も定説はない。黒幕説の多くは同時代史料の裏付けが乏しい。信長の遺体は発見されておらず、これも諸説を生む一因となっている。事件は政権中枢の突然の空白を生み、以後の主導権争いは山崎、清洲会議、賤ヶ岳（1583）、小牧・長久手（1584）と続いた。なお京都の市中はこの前後にも軍勢の往来と略奪の被害を受けており、町衆や寺社は自衛と交渉を重ねている。',
    },
    year: 1582,
    keywords: ['本能寺の変', '織田信長', '明智光秀', '山崎の戦い', '中国大返し', '羽柴秀吉'],
    caveat:
      '明智光秀が織田信長を討った理由には定説がありません。怨恨説・野望説・四国説のほか、朝廷や足利義昭、徳川家康などを黒幕とする説が唱えられてきましたが、黒幕説の多くは同時代の史料に裏付けがありません。「光秀は恨みから討った」と断定する説明は、江戸時代に書かれた読み物に多く由来します。信長の遺体が見つかっていないことも、後世にさまざまな話を生みました。',
    gameNote:
      '盤上では docs/01 §7 の指定に従い「天下点1位のプレイヤーが兵の半数と領国1つを失う」という効果にしています。これは「独走していた者が突然崩れる」という出来事の形を遊びに翻訳したものであって、明智光秀が首位を狙う役回りだったという意味ではありません。上に書いたとおり光秀の動機には定説がなく、ゲームの効果を動機の説明と読まないでください。また実際には信長個人だけでなく嫡男の信忠も同時に失われたことが、その後の混乱の大きな理由でした。盤上ではこれを「兵の半減と領国1つの喪失」という一つの効果にまとめています。',
  },
};

// ---------------------------------------------------------------------------
// 1582 — 太閤検地（時代法）
// ---------------------------------------------------------------------------

/**
 * 章の開始時に必ず発動する時代法。
 * 「全領国の石高が再評価される」（docs/01 §7）を、
 * 検地をどう受けるかの選択＋守りの段階補正で表す。
 *
 * formationMod defend -1 は、一地一作人と在地武士の城下集住によって
 * 村の側の自前の武力が薄くなったことを、合戦のルールに落としたもの。
 */
export const TAIKO_KENCHI: HistoricalEvent = {
  id: 'ev:taiko-kenchi',
  year: 1582,
  chapter: 4,
  kind: 'era-law',
  name: '太閤検地',
  reading: 'たいこうけんち',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '竿を入れて検地帳に登録する（石高+2、費用として銭-1）',
              then: [
                { t: 'gain', res: 'koku', n: 2 },
                { t: 'lose', res: 'zeni', n: 1 },
              ],
            },
            {
              label: '在地の申告に任せ、争いを避ける（石高+1、軍略+1）',
              then: [
                { t: 'gain', res: 'koku', n: 1 },
                { t: 'gain', res: 'gunryaku', n: 1 },
              ],
            },
          ],
        },
      ],
    },
    // 一地一作人と在地武士の城下集住により、村の側の自前の武力が薄くなる
    { t: 'holeCardMod', n: -1, scope: 'defend' },
  ],
  note: {
    summary:
      '1582年から全国で進められた土地の調査。ものさしと枡を統一して田畑の面積と等級を測り、収穫量を米の量（石高）で表した。耕地ごとに耕作者を検地帳へ書き上げた。',
    detail: {
      1: '1582年から、豊臣秀吉は田畑の広さや土地のよしあしを全国で調べさせました。ものさしと枡（ます）を同じものにそろえ、とれる米の量を「石高（こくだか）」であらわしました。だれがその田を耕しているかも帳面に書きました。',
      2: '天正10年(1582)、羽柴（豊臣）秀吉が山城国で行った検地に始まり、以後およそ1598年まで各地で進められた土地調査。6尺3寸四方を1歩、300歩を1段とする基準を用い、田畑を上・中・下・下々の等級に分け、枡を京枡に統一して収穫量を米の量＝石高で表した。それまで各地で用いられていた銭を単位とする貫高制から、石高制への移行が進んだ。検地帳には耕地一筆ごとに耕作者の名が記され（一地一作人）、年貢を納める者が定まった。この結果、荘園以来の重なり合った土地の権利が整理される一方、中間で得ていた収入を失う人びとも生じ、各地で検地に対する抵抗が起きている。石高は大名に課す軍役の基準にもなった。',
      3: '天正10年(1582)、山崎の戦いの直後に羽柴秀吉が山城国で実施した検地を出発点とし、豊臣政権のもとで慶長3年(1598)頃まで各地で継続された土地調査の総称。施行原則は、6尺3寸四方を1歩、300歩を1段とし（それ以前の360歩1段から改めた地域が多い）、田畑・屋敷地を上・中・下・下々に等級づけ、枡を京枡に統一して1段あたりの標準収穫量（石盛）を定め、面積に乗じて石高を算出するというものである。検地帳には一筆ごとに耕作者名を記載し、その者を年貢負担者として確定した（一地一作人の原則）。これにより荘園制以来の作職・下作職などの重層的な権利は整理に向かい、名主層や寺社が中間で得ていた得分は縮小した。石高は大名への軍役賦課の基準ともなり、「何万石だから何人の兵」という換算が全国規模で成立する。ただし検地の実施は一律ではなく、時期・地域・担当奉行によって基準や厳しさに差があったことが知られており、「太閤検地によって全国が一挙に均質化した」という理解は近年見直されている。指出（さしだし＝在地からの申告）に依った地域も残る。検地帳には女性の名で登録された例も確認されており、村の運営には男女双方が関わっていた。検地に対しては各地で反発があり、実施をめぐって一揆に至った地域もある。在地の武士が城下へ集められ、村に残るのが百姓であるという区分が進んだ点で、検地は次の刀狩・人掃令と一続きの政策と見るのが一般的である。',
    },
    year: [1582, 1598],
    keywords: ['太閤検地', '石高制', '一地一作人', '京枡', '検地帳', '貫高制'],
    caveat:
      '「太閤検地で全国の土地が同じ基準に統一された」という説明は単純化です。実際には時期・地域・担当した奉行によって基準や厳しさに差があり、在地からの申告（指出）に依った地域も残りました。また一地一作人によって百姓の権利が一律に強まった、あるいは一律に弱まった、といった評価にも研究者の間で幅があります。開始年を1582年とするのは山城国の検地を起点とする数え方で、豊臣政権以前から各地の大名が検地を行っていました。',
    gameNote:
      '盤上では全員が「厳しく竿を入れる」か「在地の申告に任せる」かを選ぶ形にしていますが、実際の検地は領主が選べる二択ではなく、豊臣政権の側から課された政策でした。二択にしたのは、検地に伴う費用と在地との摩擦を遊びとして判断させるための単純化です。また「守り手の役が1段階下がる」という効果は、在地の武士が城下に集められ村の側の自前の武力が薄くなったことを合戦のルールに翻訳したもので、検地を受けた国が実際に攻め落とされやすくなったという記録があるわけではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1587 — バテレン追放令（事件札）
// ---------------------------------------------------------------------------

/**
 * 宗教を扱うので、効果は「どちらを選んでも失うものがある」二択に留める。
 * 令が出た後も南蛮貿易は続いた、という史実をそのまま二択の形にしてある。
 */
export const BATEREN_TSUIHOUREI: HistoricalEvent = {
  id: 'ev:bateren-tsuihourei',
  year: 1587,
  chapter: 4,
  kind: 'incident',
  name: 'バテレン追放令',
  reading: 'ばてれんついほうれい',
  effects: [
    {
      t: 'choice',
      options: [
        {
          label: '宣教師の退去を命じ、公儀の下知に従う（銭-2、威信+1）',
          then: [
            { t: 'lose', res: 'zeni', n: 2 },
            { t: 'gain', res: 'ishin', n: 1 },
          ],
        },
        {
          label: '南蛮船の来航を優先し、布教を黙認する（銭+2、威信-1）',
          then: [
            { t: 'gain', res: 'zeni', n: 2 },
            { t: 'lose', res: 'ishin', n: 1 },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1587年、九州平定の途上で豊臣秀吉が宣教師（バテレン）の国外退去を命じた。ただし南蛮船の来航と貿易は認められ、令が徹底されることはなかった。',
    detail: {
      1: '1587年、豊臣秀吉は九州で、キリスト教を広めにきた宣教師に日本から出ていくよう命じました。ただし外国の船が来て品物を売り買いすることは、そのまま認めました。そのため命令はきびしく守られませんでした。',
      2: '天正15年6月（1587年7月）、九州平定を終えた豊臣秀吉が博多で出した命令。宣教師（バテレン）に20日以内の国外退去を命じる一方、南蛮船の来航と貿易は従来どおり認めるとした。背景には、大村純忠がイエズス会に長崎を寄進していたこと、キリシタンとなった大名や領民のもとで神社仏閣が壊された例があったこと、日本人が海外へ売られていたことなどが挙げられている。ただし貿易と布教が一体であったため退去は徹底されず、宣教師の多くは国内に残った。1596年のサン=フェリペ号事件をきっかけに秀吉の態度は厳しくなり、翌1597年には長崎で26人が処刑されている（日本二十六聖人）。',
      3: '天正15年6月19日（1587年7月24日）、九州平定を終えた豊臣秀吉が筑前箱崎で発した五箇条の覚書。宣教師（伴天連＝バテレン）に対し20日以内の国外退去を命じるものだが、同時に出された文書では「黒船（南蛮船）の来航は商売のことなので格別であり、今後も来るように」と明示され、貿易は維持された。前日付の十一箇条の覚書も伝わり、二つの文書の関係については研究上の議論がある。発令の背景としては、大村純忠によるイエズス会への長崎寄進、キリシタン大名の領内での神社仏閣の破壊、日本人がポルトガル商人を介して海外へ売られていたこと、そして九州平定によって秀吉が現地の状況を直接知ったことなどが指摘される。もっとも貿易船の来航と宣教師の活動は実際上分かちがたく、退去の実施は徹底されなかった。宣教師の多くは潜伏し、あるいは貿易の通詞として滞在を続けている。慶長元年(1596)のサン=フェリペ号事件を機に方針は厳しくなり、翌年京都・大坂で捕らえられたフランシスコ会士・イエズス会士と日本人信徒あわせて26人が長崎で処刑された（日本二十六聖人）。以後の禁教政策は江戸幕府に引き継がれる。なお当時のキリスト教信徒には、大名や武士だけでなく、商人・職人・百姓、そして多くの女性が含まれていた。この出来事は信仰の正誤を論じる事柄ではなく、交易・外交・在地の宗教秩序が絡み合った政治的な事件として理解する必要がある。',
    },
    year: 1587,
    keywords: ['バテレン追放令', '豊臣秀吉', 'イエズス会', '南蛮貿易', '長崎', '日本二十六聖人'],
    caveat:
      'バテレン追放令は「秀吉がキリスト教を禁止した」と短く説明されることが多いのですが、同時に南蛮船の来航と貿易は認められており、宣教師の退去も徹底されませんでした。発令の理由についても、長崎の寄進を問題視したとする説、日本人の海外売買を問題視したとする説、九州の政治秩序を整えるためとする説などがあり、一つに定まっていません。同じ時期に出されたとされる二つの覚書の関係にも議論があります。',
    gameNote:
      '盤上では「退去を命じて銭を失うか、黙認して威信を失うか」の二択にしていますが、実際の大名がこの二択を自由に選べたわけではありません。二択にしたのは、貿易の利益と公儀への恭順が両立しにくかった状況を遊びとして判断させるための単純化です。威信の増減は豊臣政権から見た立場を表す数値であり、どちらの選択が正しいかを示すものではありません。',
  },
};

// ---------------------------------------------------------------------------
// 1588 — 刀狩（時代法）
// ---------------------------------------------------------------------------

/**
 * deckRemove の唯一の使用箇所。軍札から僧兵が抜け、
 * 65枚 → 59枚 になって手役の出やすさが変わる（docs/06 §2）。
 * 鉄砲伝来（第II章）で増えた札が、ここで初めて減る。
 *
 * ratio は「間引く割合」。docs/06 §2 の 13枚→7枚 に合わせて実装すること。
 */
export const KATANAGARI: HistoricalEvent = {
  id: 'ev:katanagari',
  year: 1588,
  chapter: 4,
  kind: 'era-law',
  name: '刀狩',
  reading: 'かたながり',
  effects: [
    // 僧兵札を半減させる。docs/06 §2 では 13枚 → 7枚（デッキ 65枚 → 59枚）
    { t: 'deckRemove', arm: 'souhei', ratio: 0.5 },
    // 集めた刀を方広寺大仏の釘・鎹にすると謳われた。名分としての威信
    { t: 'target', who: 'all', then: [{ t: 'gain', res: 'ishin', n: 1 }] },
  ],
  note: {
    summary:
      '1588年、豊臣秀吉が百姓の刀・脇差・弓・槍・鉄砲などの所持を禁じた三箇条の令。集めた武器は京都方広寺の大仏の釘や鎹に使うと定められた。',
    detail: {
      1: '1588年、豊臣秀吉は百姓が刀や弓、鉄砲を持つことを禁じました。集めた武器は、京都に建てる大きな大仏の釘（くぎ）などに使うと言われました。百姓は田や畑の仕事に、武士はいくさに、と役わりが分けられていきました。',
      2: '天正16年7月8日（1588年8月29日）に出された三箇条の法令。第一条で百姓が刀・脇差・弓・槍・鉄砲などの武具を持つことを禁じ、第二条で没収した武器は京都方広寺の大仏建立の釘や鎹に用いると述べ、第三条で百姓は農具だけを持って耕作に励むよう命じた。武器を仏の供養のために使うという名分を掲げた点に特徴がある。前年に肥後で起きた国人一揆など、在地の反乱への対応が背景にあった。検地（1582-）とあわせて、武士と百姓の身分を分ける兵農分離が進み、村が自ら武力で争いを解決する道が細くなった。ただし実際には村に鉄砲や刀が残り、獣害対策や自衛のために使われ続けたことが記録から分かっている。',
      3: '天正16年7月8日（1588年8月29日）付で豊臣秀吉が発した三箇条の法令。第一条は諸国の百姓が刀・脇指・弓・槍・鉄炮その他の武具を所持することを禁じ、年貢を難じ一揆を企てる者を取り締まるとする。第二条は没収した武具を京都方広寺大仏の釘・鎹に用いるとし、百姓は現世のみならず来世まで救われると説く。第三条は農具のみを持って耕作に専念すれば子々孫々まで無事であるとする。直接の契機としては、天正15年(1587)の肥後国人一揆をはじめとする在地の抵抗が挙げられる。刀狩は太閤検地・身分統制令・人掃令と一連の政策をなし、武士と百姓の身分的分離（兵農分離）を制度として固める役割を果たした。ただし藤木久志らの研究が明らかにしたように、刀狩によって村から武器が消えたわけではない。江戸時代を通じて村には鉄砲（威鉄砲・猟師鉄砲）や刀が残り、獣害対策や自衛、祭礼に用いられている。刀狩の実質は物理的な武装解除というより、百姓が武器を「身に帯びて人前に出る」ことを禁じ、武力による自力救済を公儀の裁定へ移す点にあったと理解されている。なお、この時期の村では女性も含めた村人が寄合を開いて年貢や用水を交渉しており、村の自治そのものが失われたわけではない。方広寺の大仏は文禄5年(1596)の地震で大きく損なわれ、その後も再建と焼失を繰り返した。',
    },
    year: 1588,
    keywords: ['刀狩', '豊臣秀吉', '兵農分離', '方広寺', '一揆', '村の武器'],
    caveat:
      '「刀狩によって百姓から武器がなくなった」という説明は、藤木久志らの研究によって大きく見直されています。江戸時代を通じて村には鉄砲や刀が残り、獣害対策や自衛に使われていました。刀狩の実質は、武器そのものを根こそぎ取り上げることよりも、百姓が武器を帯びて争いを自力で解決することを禁じ、争いの裁定を公儀へ移す点にあったと考えられています。',
    gameNote:
      '盤上では軍札から僧兵札を半減させています（docs/06 §2 では13枚→7枚、デッキ全体が65枚から59枚へ）。刀狩令が名指しで対象としたのは百姓の武具であり、僧兵の武装解除そのものを定めた法令ではありません。僧兵札を減らすのは、寺社の武力が政権の統制下に入っていく時代の変化を、軍札の中身という形で体感してもらうための翻訳です。また全員の威信+1 は「集めた刀を大仏の釘にする」という名分が掲げられたことを表したもので、刀狩を良い政策だと評価するものではありません。docs/01 §7 にある「一揆・災害マスの効果が半減」はマス側の処理で扱い、本データには含めていません。',
  },
};

// ---------------------------------------------------------------------------
// 1590 — 小田原攻め（事件札）
// ---------------------------------------------------------------------------

/**
 * seize free:true を使う。長い包囲の末の開城を
 * 「兵を大きく費やすが、兵を使わずに国が1つ手に入る」形に翻訳した。
 */
export const ODAWARA_SEIBATSU: HistoricalEvent = {
  id: 'ev:odawara-seibatsu',
  year: 1590,
  chapter: 4,
  kind: 'incident',
  name: '小田原攻め',
  reading: 'おだわらぜめ',
  effects: [
    // 数か月にわたる包囲。兵糧と人手を費やす
    { t: 'lose', res: 'hei', n: 2 },
    // 開城により、兵を用いずに国を1つ切り取る
    { t: 'target', who: 'self', then: [{ t: 'seize', free: true }] },
  ],
  note: {
    summary:
      '1590年、豊臣秀吉が大軍で小田原城を包囲した。約3か月の対陣の末に北条氏直が降伏し、北条氏の関東支配は終わった。続いて奥州仕置が行われた。',
    detail: {
      1: '1590年、豊臣秀吉は大きな軍をひきいて、北条氏の小田原城をとりかこみました。城のまわりには長い堀と土塁がめぐらされていましたが、3か月ほどで北条氏は降参しました。このあと東北の大名たちも秀吉にしたがい、大きな戦は終わりに向かいます。',
      2: '天正18年(1590)、豊臣秀吉が20万ともいわれる軍勢を動かして相模の小田原城を包囲した。前年、北条方の武将が真田氏の名胡桃城を奪ったことが、大名間の私戦を禁じる秀吉の方針に反するとされたのが直接の契機である。小田原城は城下町全体を堀と土塁で囲む総構えを持ち、城内には武士だけでなく町人や周辺の百姓も入って籠城した。秀吉方は周辺の支城を落としつつ長期の包囲を続け、石垣山に陣城を築いた。約3か月後に北条氏直が降伏し、父の氏政と叔父の氏照は切腹、氏直は高野山へ送られた。続いて秀吉は会津へ向かい、東北の大名の所領を確定する奥州仕置を行った。徳川家康はこの後、関東へ移された。',
      3: '天正18年(1590)3月から7月にかけて行われた、豊臣秀吉による関東の後北条氏への攻撃。前年、北条方の猪俣邦憲が上野の名胡桃城を真田方から奪った事件が、大名間の私戦を禁じる秀吉の方針（いわゆる惣無事）に反するとして開戦の名分とされた。動員兵力は20万を超えたと伝えられるが、これは後世の記録に基づく数字で厳密ではない。小田原城は城下町ごと全長9キロメートル近い堀と土塁で囲む総構えを備え、籠城には家臣団のほか町人・周辺の百姓も加わった。豊臣方は山中城など支城を短期間で攻略しつつ包囲を続け、笠懸山に石垣山城を築いた（後世「一夜城」と呼ばれるが、実際には数十日を要した本格的な石垣造りの城である）。7月5日に北条氏直が降伏し、氏政・氏照は切腹、氏直は高野山へ追われた。続く奥州仕置では、参陣しなかった大名の所領が没収され、伊達政宗は会津を没収されるなど東北の勢力配置が組み替えられた。仕置に反発した葛西大崎一揆・九戸政実の乱（1591）も起きている。徳川家康は旧北条領へ移封され、江戸を本拠とした。なお開戦の前提とされる「惣無事令」については、藤木久志が提示した法令としての枠組みに対し、竹井英文らから単一の法令の存在を疑問視する批判が出ており、研究上の議論が続いている。長期の包囲は周辺の村々に兵糧の徴発や耕作の中断をもたらし、その負担は記録に残りにくい形で人びとにかかっていた。',
    },
    year: 1590,
    keywords: ['小田原攻め', '北条氏', '総構え', '石垣山城', '奥州仕置', '惣無事'],
    caveat:
      'この出来事は「小田原征伐」とも呼ばれますが、「征伐」は攻めた側の立場に立った呼び方なので、ここでは「小田原攻め」としています。「秀吉が惣無事令という法令を出し、それに違反したから北条を攻めた」という説明は、近年の研究で見直されています。藤木久志が提示した惣無事令の枠組みに対し、単一の法令として存在したかを疑問視する批判があり、議論が続いています。また「20万の大軍」「石垣山の一夜城」といった数字や逸話も後世の記録によるもので、石垣山城は実際には数十日をかけて築かれた石垣造りの城です。',
    gameNote:
      '盤上では「兵を2失い、そのうえで兵を使わずに国を1つ切り取る」という効果にしています。これは長い包囲で兵糧と人手を費やしながら、最後は戦わずに城が開かれた、という経過を遊びに翻訳したものです。docs/01 §7 にある「関東の国の防御-2」は特定地域を指す効果で、現在の Effect 型では地域を指定できないため、この形に置き換えました。実際の降伏は攻める側が選べる結果ではなく、支城の攻略・兵糧・外交が積み重なった末のものです。',
  },
};

// ---------------------------------------------------------------------------
// 1591 — 身分統制令と人掃令（時代法）
// ---------------------------------------------------------------------------

/**
 * 本作の逆転装置（下剋上）が閉じる瞬間。
 * grantGekokujo に負の値を渡し、全員から下剋上札を1枚取り上げる。
 * 第I章から積み上げてきた「身分は実力で覆せる」という手触りが、
 * ここで制度によって塞がれる。
 */
export const MIBUN_TOUSEI: HistoricalEvent = {
  id: 'ev:mibun-tousei',
  year: 1591,
  chapter: 4,
  kind: 'era-law',
  name: '身分統制令と人掃令',
  reading: 'みぶんとうせいれいとひとばらいれい',
  effects: [
    // n が負の grantGekokujo は「下剋上札を取り上げる」。身分が固定される
    { t: 'target', who: 'all', then: [{ t: 'grantGekokujo', n: -1 }] },
    // 村ごとの家数と人数が把握され、年貢と軍役が確実に取れるようになる
    { t: 'target', who: 'all', then: [{ t: 'gain', res: 'koku', n: 1 }] },
  ],
  note: {
    summary:
      '1591年、武士が百姓や町人になること、百姓が田畑を離れて商いや日雇いに移ることなどを禁じた三箇条の令が出された。翌年には村ごとに家数と人数を調べる人掃令が続いた。',
    detail: {
      1: '1591年、豊臣秀吉は「武士が百姓や町人になってはいけない」「百姓が田畑をすてて商売に移ってはいけない」と決めました。つぎの年には、村ごとに家の数や人の数を調べさせました。だれがどの身分かがはっきりと決められていきました。',
      2: '天正19年(1591)に出された三箇条の法令（身分統制令）は、武家に仕える侍・中間・小者が百姓や町人になることを禁じ、百姓が田畑を離れて商いや日雇いに移ることを禁じ、他の武家が奉公先を離れた者を召し抱えることも禁じた。翌天正20年(1592)には、関白豊臣秀次の名で人掃令が出され、村ごとに家数・人数・男女・年齢・身分を調べて書き上げさせた。いずれも朝鮮出兵に必要な人手と年貢を確保する目的があったとされる。検地(1582-)・刀狩(1588)と合わせ、武士・百姓・町人という身分の区分が制度として固まり、実力で身分を越える下剋上の余地は狭まっていった。',
      3: '天正19年(1591)8月に出された三箇条の法令は、一般に身分統制令と呼ばれる。内容は、(1)侍・中間・小者などの武家奉公人が百姓や町人になることの禁止、(2)百姓が田畑を放棄して商売や賃仕事に転じることの禁止、(3)奉公先を離れた者を他の武家が召し抱えることの禁止、である。翌天正20年(1592)3月には、関白豊臣秀次の名で人掃令（戸口調査令）が発せられ、村・町ごとに家数、人数、男女、年齢、身分（奉公人・百姓・町人）を書き上げて提出させた。これらは文禄の役に向けた動員と兵糧の確保を直接の目的としたとみられるが、結果として太閤検地・刀狩と一続きの兵農分離政策を完成させ、近世の身分制度の土台となった。ただし「身分統制令」「人掃令」という呼称は後世の歴史学が与えたもので、当時そう呼ばれていたわけではない。また身分の固定は文言どおりに実現したわけではなく、村から町へ移る者、武家奉公に出る百姓、帰農する武士は近世を通じて存在し続けた。人掃令によって作成された帳面は、村に暮らす男女と子どもの数を記録した点で、当時の人口を知る手がかりにもなっている。武力による立身の道が制度として狭まったことは、戦国期の下剋上を支えた条件が失われたことを意味する。',
    },
    year: [1591, 1592],
    keywords: ['身分統制令', '人掃令', '豊臣秀次', '兵農分離', '身分', '戸口調査'],
    caveat:
      '「身分統制令」「人掃令」という名前は後世の歴史学が付けたもので、当時の呼び名ではありません。またこれらの法令で身分がただちに固定されたわけでもなく、近世を通じて村から町へ移る者、武家奉公に出る百姓、帰農する武士は存在し続けました。法令の文言と実際の社会の動きは分けて考える必要があります。',
    gameNote:
      '盤上では全員から下剋上札を1枚取り上げる効果にしています（docs/06 §6）。これは「実力で身分を覆す道が制度として狭まった」ことを、本作の逆転装置そのものを閉じる形で体感してもらうための翻訳です。実際には法令によって下剋上が一夜で消えたわけではありません。また下剋上札の配布は下位のプレイヤーほど多い救済機構なので、この時代法は下位に厳しく働きます。バランス上きつすぎる場合は「最下位は取り上げない」といった調整を検討してください。',
  },
};

// ---------------------------------------------------------------------------
// 1592 — 文禄の役（事件札）
// ---------------------------------------------------------------------------

/**
 * holeCardMod を使い、「兵が海の向こうにいる」状態を
 * 合戦の手勢札の枚数として盤上に残す。
 * 戦地の被害を書き落とさないこと。docs/05 §4-3。
 */
export const BUNROKU_NO_EKI: HistoricalEvent = {
  id: 'ev:bunroku-no-eki',
  year: 1592,
  chapter: 4,
  kind: 'incident',
  name: '文禄の役',
  reading: 'ぶんろくのえき',
  effects: [
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '渡海の軍役に応じる（兵-3、威信+1、以後この章の合戦で手勢札-1）',
              then: [
                { t: 'lose', res: 'hei', n: 3 },
                { t: 'gain', res: 'ishin', n: 1 },
                { t: 'holeCardMod', n: -1 },
              ],
            },
            {
              label: '国元にとどまり、普請役と兵糧を負担する（銭-3、威信-1）',
              then: [
                { t: 'lose', res: 'zeni', n: 3 },
                { t: 'lose', res: 'ishin', n: 1 },
              ],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1592年、豊臣秀吉が明の征服を掲げて朝鮮へ軍を送った。朝鮮では各地で義兵が立ち、明の援軍も入って戦線は膠着し、1593年に休戦した。戦地の被害は甚大だった。',
    detail: {
      1: '1592年、豊臣秀吉は中国（明）を従えようとして、朝鮮に大きな軍を送りました。朝鮮の人びとは各地で立ち上がり、明からも助けの軍が来て、戦いは長引きました。朝鮮の村や町は大きな被害を受け、日本の大名たちも多くの兵と米を失いました。',
      2: '文禄元年(1592)、豊臣秀吉は明の征服を掲げ、その道を貸すよう朝鮮に求めて拒まれると、約15万の軍を朝鮮へ渡らせた。日本側は釜山に上陸して漢城（ソウル）・平壌まで進んだが、李舜臣らの水軍に海上の補給路を脅かされ、各地で起きた義兵の抵抗と明の援軍によって戦線は行き詰まった。翌1593年に休戦し、講和交渉が始まる。交渉は決裂し、1597年に再び出兵が行われた（慶長の役）。朝鮮では城下や村が焼かれ、多くの人が命を落とし、陶工をはじめとする技術者や住民が日本へ連れて行かれた。日本側でも動員された大名の兵と兵糧の負担は重く、豊臣政権の力を弱める一因となった。',
      3: '文禄元年(1592)4月、豊臣秀吉は明の征服を目的に掲げ、その先導を求めて拒否した朝鮮へ約15万8千の軍を渡海させた。日本側は釜山に上陸後、短期間で漢城を占領し、加藤清正らは咸鏡道まで、小西行長らは平壌まで進んだ。しかし李舜臣が率いる朝鮮水軍によって西海岸の補給路が断たれ、各地で郭再祐らの義兵が蜂起し、明が李如松の率いる援軍を送ったことで戦線は膠着した。1593年正月の平壌の戦い、碧蹄館の戦い、晋州城の攻防などを経て休戦に入り、以後は講和交渉が続く。交渉は明側と日本側で条件の理解が食い違ったまま進み、慶長2年(1597)に決裂して再度の出兵（慶長の役）に至った。翌1598年の秀吉の死により日本軍は撤退する。この戦争が朝鮮の社会に与えた被害は極めて大きく、農地の荒廃、人口の減少、文化財の焼失が広範囲に及んだ。多数の人びとが捕らえられて日本へ連行され、そのなかには有田焼・薩摩焼・萩焼の礎を築いた陶工が含まれる。日本国内でも、大名は石高に応じた軍役を課され、兵と兵糧、船と水夫の負担が村々にかかった。名護屋城の築城や渡海のための普請役も広く動員された。この戦争を日本側の武功の物語として語ることは、戦地となった朝鮮半島の人びとの被害を見落とすことになる。学習用の記述としては、双方に何が起き、誰が負担を負ったのかを併記することが求められる。',
    },
    year: [1592, 1598],
    keywords: ['文禄の役', '慶長の役', '朝鮮出兵', '李舜臣', '義兵', '軍役'],
    caveat:
      '出兵の目的については、明の征服という秀吉の主張のほか、国内の大名に与える恩賞地を海外に求めたとする説、東アジアの交易秩序への関与を狙ったとする説などがあり、単一の説明には定まっていません。動員兵力の数字も史料によって幅があります。日本では「文禄・慶長の役」、朝鮮・韓国では「壬辰倭乱・丁酉再乱」、中国では「万暦朝鮮の役」と呼ばれ、呼称そのものが立場によって異なります。',
    gameNote:
      '盤上では「渡海の軍役に応じる」か「国元にとどまる」かの二択にし、応じた場合は以後この章の合戦で手勢札が1枚減る形にしています。これは主力が海の向こうにいる状態を合戦のルールに翻訳したものです。実際には軍役は豊臣政権から課されるもので、大名が自由に断れるものではありませんでした。二択にしたのは負担のかかり方の違いを遊びとして体験させるための単純化です。また威信+1 は豊臣政権から見た立場を表す数値であり、出兵を評価するものではありません。docs/01 §7 では「九州の領主は兵を派遣」とありますが、地域を指定する効果が現在の Effect 型にないため、全員の選択としています。',
  },
};

// ---------------------------------------------------------------------------
// 1598 — 秀吉の死（事件札）
// ---------------------------------------------------------------------------

/**
 * 首位の威信を削り、全員に下剋上札を配る。
 * MIBUN_TOUSEI で一度閉じた逆転装置が、
 * 中央の統制が緩んだことで再び開く、という順序にしてある。
 */
export const HIDEYOSHI_NO_SHI: HistoricalEvent = {
  id: 'ev:hideyoshi-no-shi',
  year: 1598,
  chapter: 4,
  kind: 'incident',
  name: '秀吉の死と五大老',
  reading: 'ひでよしのしとごたいろう',
  effects: [
    // 中央の権威が揺らぐ。首位ほど後ろ盾を失う
    { t: 'target', who: 'leader', then: [{ t: 'lose', res: 'ishin', n: 2 }] },
    // 合議による統制の隙間から、再び実力で覆す余地が生まれる
    { t: 'target', who: 'all', then: [{ t: 'grantGekokujo', n: 1 }] },
  ],
  note: {
    summary:
      '1598年9月、豊臣秀吉が伏見城で死去した。幼い秀頼を五大老・五奉行が支える体制が敷かれ、朝鮮からの撤退が進む一方、大名間の対立が表面化していった。',
    detail: {
      1: '1598年、豊臣秀吉が病気で亡くなりました。あとつぎの秀頼はまだ幼く、徳川家康など有力な大名たちが話し合って政治を進めることになりました。朝鮮に送られていた軍も引きあげました。やがて大名どうしの対立が強まっていきます。',
      2: '慶長3年8月18日（1598年9月18日）、豊臣秀吉が伏見城で死去した。後継の秀頼は6歳であったため、徳川家康・前田利家・毛利輝元・宇喜多秀家・上杉景勝の五大老と、石田三成ら五奉行が政務を担う体制が置かれた。秀吉の死は伏せられたまま朝鮮からの撤退が進められた。翌1599年に前田利家が没すると、大名間の対立が表面化し、五大老筆頭の徳川家康が諸大名との縁組や所領の裁定を独自に進めたことに石田三成らが反発する。この対立が1600年の関ヶ原の戦いへとつながっていった。',
      3: '慶長3年8月18日（1598年9月18日）、豊臣秀吉が伏見城で死去した。享年62とされる。嫡子秀頼は6歳であり、秀吉は生前から徳川家康・前田利家・毛利輝元・宇喜多秀家・上杉景勝の五大老（当初は小早川隆景を含む六名とする理解もある）と、浅野長政・増田長盛・石田三成・前田玄以・長束正家の五奉行による合議体制を整えていた。もっとも「五大老」「五奉行」という呼称は後世に定着したもので、当時の文書では「御奉行衆」「年寄」などと記されることが多い。死は当面秘され、朝鮮に在陣していた諸軍の撤退が優先して進められた。撤退は同年末までにおおむね完了する。慶長4年(1599)閏3月に前田利家が没すると、家康と石田三成ら奉行衆との対立が急速に表面化した。家康は秀吉の遺命に反するとされた大名間の私的な縁組を進め、伏見・大坂で主導権を握っていく。慶長5年(1600)、会津の上杉景勝に上洛を求めて拒まれたことを名分に家康が東へ軍を進め、その隙に三成らが挙兵したことで、諸大名は東西に分かれた。合議体制は、秀吉個人の権威に依存していた豊臣政権の構造をそのまま引き継いだため、その死とともに機能を保てなくなったと評価されることが多い。なお淀殿をはじめとする秀頼の周囲の女性たちは、この時期の政治的な交渉と縁組に深く関与していた。',
    },
    year: 1598,
    keywords: ['豊臣秀吉', '五大老', '五奉行', '豊臣秀頼', '徳川家康', '石田三成'],
    caveat:
      '「五大老」「五奉行」という呼び名は後世に定着したもので、当時の文書では「年寄」「御奉行衆」などと書かれています。構成人数の数え方にも異説があります。また関ヶ原に至る過程を「家康の野心」と「三成の忠義」の対立として語る説明は、江戸時代以降に形づくられた図式を含んでおり、実際には各大名それぞれの所領と家の存続をめぐる判断が絡んでいました。',
    gameNote:
      '盤上では「天下点1位の威信-2」「全員に下剋上札1枚」という効果にしています。これは中央の統制が緩み、首位の後ろ盾が失われて再び実力が物を言う状況になったことを遊びに翻訳したものです。特定の人物の死を有利・不利として扱うのは遊びの都合であり、史実の評価ではありません。直前の時代法「身分統制令と人掃令」で取り上げた下剋上札が、ここで一度だけ戻る形にしてあります。',
  },
};

// ---------------------------------------------------------------------------
// 1600 — 関ヶ原の戦い（終章）
// ---------------------------------------------------------------------------

/**
 * docs/01 §7 では終章として第四章から分けられているため chapter: 5 とする。
 * 東軍・西軍の勝敗判定そのものは終章のルール（総戦力の比較）が担うので、
 * ここでは「陣営を選ぶ」ことと「寝返りの余地が全員に配られる」ことだけを効果にする。
 */
export const SEKIGAHARA: HistoricalEvent = {
  id: 'ev:sekigahara',
  year: 1600,
  chapter: 5,
  kind: 'incident',
  name: '関ヶ原の戦い',
  reading: 'せきがはらのたたかい',
  effects: [
    // 寝返りが勝敗を分けた。全員に逆転の一手が渡る（docs/06 §6）
    { t: 'target', who: 'all', then: [{ t: 'grantGekokujo', n: 1 }] },
    // 陣営の選択。勝敗は終章のルール（総戦力の比較）で解決する
    {
      t: 'target',
      who: 'all',
      then: [
        {
          t: 'choice',
          options: [
            {
              label: '東軍に付く',
              then: [{ t: 'gain', res: 'gunryaku', n: 1 }],
            },
            {
              label: '西軍に付く',
              then: [{ t: 'gain', res: 'gunryaku', n: 1 }],
            },
          ],
        },
      ],
    },
  ],
  note: {
    summary:
      '1600年10月21日、美濃国関ヶ原で徳川家康を中心とする東軍と、石田三成らの西軍が戦った。西軍が敗れ、以後の政治の主導権は家康が握った。',
    detail: {
      1: '1600年、いまの岐阜県の関ヶ原で、徳川家康を中心とする東軍と、石田三成たちの西軍が戦いました。とちゅうで西軍から東軍に寝返る大名が出て、西軍は負けました。このあと家康が政治の中心になり、江戸幕府がひらかれます。',
      2: '慶長5年9月15日（1600年10月21日）、美濃国不破郡関ヶ原（現在の岐阜県関ケ原町）で行われた合戦。会津の上杉景勝を討つため東へ向かった徳川家康に対し、石田三成らが大坂で挙兵したことから、諸大名が東西に分かれた。西軍は毛利輝元を総大将として掲げたが、輝元は大坂城にとどまり戦場には出ていない。合戦では松尾山に布陣していた小早川秀秋が東軍側について西軍の側面を突き、西軍は総崩れとなった。三成らは捕らえられて処刑され、西軍に付いた大名の多くは所領を没収または削減された。家康は1603年に征夷大将軍となり、江戸幕府を開く。',
      3: '慶長5年9月15日（1600年10月21日）、美濃国不破郡関ヶ原を主戦場として行われた合戦。会津の上杉景勝の上洛拒否を名分に家康が東下した隙に、石田三成・大谷吉継らが挙兵し、毛利輝元を総大将に擁立したことで、諸大名は東西に分かれた。輝元自身は大坂城に在城し、戦場には現れていない。前哨戦として伏見城の攻防、伊勢・北陸・九州・東北など各地で戦闘が並行しており、関ヶ原の一戦だけで天下が決したわけではない。本戦では松尾山の小早川秀秋が東軍に応じて大谷勢を攻め、脇坂安治・小川祐忠・朽木元綱・赤座直保も東軍に転じたことで西軍は崩壊した。戦後、西軍側の大名は改易・減封され、その所領が東軍側に再配分されたことが、江戸幕府の大名配置の基礎となる。合戦の経過については近年大きな見直しが進んでいる。白峰旬らは一次史料に近い記録を重視し、小早川秀秋の離反は開戦直後で、戦闘は1〜2時間程度で決着したとする見方を示している。従来語られてきた「昼まで一進一退の激戦が続き、家康が松尾山へ鉄砲を撃ちかけて秀秋を促した」という筋書きは、江戸時代後期の軍記や近代の戦史叙述に由来する部分が大きい。よく図示される布陣図も、明治期に整理されたものが元になっている。なお合戦の前後には周辺の村々が兵の通行と徴発を受け、戦場となった地域では落ち武者狩りも行われた。勝敗の記録の背後に、そこに住む人びとの生活があったことは押さえておきたい。',
    },
    year: 1600,
    keywords: ['関ヶ原の戦い', '徳川家康', '石田三成', '小早川秀秋', '東軍', '西軍'],
    caveat:
      '関ヶ原の戦いの経過は近年大きく見直されています。白峰旬らの研究では、小早川秀秋の離反は開戦直後で、戦闘は1〜2時間ほどで決着したとされます。「昼まで激戦が続き、家康が松尾山へ鉄砲を撃って秀秋を促した」という有名な場面は、江戸時代後期の軍記や近代の戦史叙述に由来する要素が大きく、同時代の史料では確かめられません。よく見る布陣図も明治期に整理されたものが元になっています。また関ヶ原の一戦だけで天下が決したわけではなく、伊勢・北陸・九州・東北でも同時に戦いが起きていました。',
    gameNote:
      '盤上では東軍・西軍を秘密に選んで同時公開し、総戦力（兵＋威信＋領国石高）の大きい側を勝ちとします（docs/01 §7）。実際の勝敗は兵力の合計で決まったわけではなく、事前の調略と当日の離反が大きく作用しました。総戦力の比較にしているのは、それまでに積み上げた実力が終章で効くようにするための遊びの設計です。ここで全員に下剋上札を1枚配るのは、寝返りが勝敗を分けたことを本作の逆転装置に翻訳したもので、特定の武将の行為を評価するものではありません。なお effects の二つの選択肢がどちらも軍略+1 なのは、陣営そのものに優劣を付けないためです。勝敗の判定は終章のルールが担い、この Effect では扱いません。',
  },
};

// ---------------------------------------------------------------------------
// 章のデッキ
// ---------------------------------------------------------------------------

/** 第IV章の開始時に必ず発動する時代法。年代順。 */
export const CHAPTER4_ERA_LAWS: readonly HistoricalEvent[] = [
  TAIKO_KENCHI,
  KATANAGARI,
  MIBUN_TOUSEI,
];

/** 第IV章の評定マスで引く事件札。年代順。 */
export const CHAPTER4_INCIDENTS: readonly HistoricalEvent[] = [
  HONNOJI_NO_HEN,
  BATEREN_TSUIHOUREI,
  ODAWARA_SEIBATSU,
  BUNROKU_NO_EKI,
  HIDEYOSHI_NO_SHI,
];

/** 第IV章のイベント全件（1582-1598）。年表表示と図鑑に使う。 */
export const CHAPTER4_EVENTS: readonly HistoricalEvent[] = [
  HONNOJI_NO_HEN,
  TAIKO_KENCHI,
  BATEREN_TSUIHOUREI,
  KATANAGARI,
  ODAWARA_SEIBATSU,
  MIBUN_TOUSEI,
  BUNROKU_NO_EKI,
  HIDEYOSHI_NO_SHI,
];

/**
 * 終章（1600）。docs/01 §7 では第四章と分けられているため、
 * chapter: 5 のイベントはこちらに置く。
 */
export const FINALE_EVENTS: readonly HistoricalEvent[] = [SEKIGAHARA];
